/**
 * Global Properties Management Page
 * 
 * Central hub for managing global properties with tabbed interface
 * for overview, properties table, relationships graph, option catalogs, and validation.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Tabs,
  Card,
  Statistic,
  Row,
  Col,
  Space,
  Typography,
  Button,
  message,
  Upload,
  Modal,
  Form,
  Input,
  Divider
} from 'antd';
import {
  DatabaseOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  ReloadOutlined,
  PlusOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { GlobalPropertiesService } from '../services/api/globalPropertiesService';
import { GlobalPropertyTableView } from '../components/GlobalProperties/GlobalPropertyTableView';
import { GlobalPropertyRelationshipGraph } from '../components/GlobalProperties/GlobalPropertyRelationshipGraph';
import { GlobalPropertyTreeView } from '../components/GlobalProperties/GlobalPropertyTreeView';
// Modal no longer needed - using split-pane mode
// import { GlobalPropertyFormModal } from '../components/GlobalProperties/GlobalPropertyFormModal';
import { OptionCatalogsTabView } from '../components/GlobalProperties/OptionCatalogsTabView';
import { TemplateManagementView } from '../components/GlobalProperties/TemplateManagementView';
import { PropertyTemplateList } from '../components/GlobalProperties/PropertyTemplateList';
import { PropertyTemplateEditor } from '../components/GlobalProperties/PropertyTemplateEditor';
import { PropertyDefinitionResponse, PropertyDefinition } from '../types/globalProperties';
import { GlobalPropertyTemplate } from '../types/propertyLevel';

const { Title, Text } = Typography;

export const GlobalPropertiesManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalProperties: 0,
    propertiesWithCatalogs: 0,
    propertiesWithDependencies: 0,
    propertiesWithValidation: 0
  });
  const [loading, setLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreatePropertyDialog, setShowCreatePropertyDialog] = useState(false);
  const [createPropertyForm] = Form.useForm();
  const [allPropertyKeys, setAllPropertyKeys] = useState<string[]>([]);
  const [allPropertyDefinitions, setAllPropertyDefinitions] = useState<PropertyDefinition[]>([]);
  const tableViewRef = useRef<{ createAndNavigateToProperty: (propertyKey: string) => Promise<void> } | null>(null);
  // Property templates state
  const [selectedPropertyTemplate, setSelectedPropertyTemplate] = useState<GlobalPropertyTemplate | null>(null);
  const [isCreatingPropertyTemplate, setIsCreatingPropertyTemplate] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      // Load all properties using the list endpoint
      // Use summary=false to get full PropertyDefinitionResponse format for statistics
      const response = await GlobalPropertiesService.listPropertyDefinitions({
        skip: 0,
        limit: 5000,
        summary: false
      });
      
      // Type guard: ensure we have PropertyDefinitionResponse format
      const properties = response.properties as PropertyDefinitionResponse[];
      
      // Extract property keys / definitions for Relationships tab
      const propertyKeys = properties.map(p => p.property_key);
      setAllPropertyKeys(propertyKeys);
      setAllPropertyDefinitions(
        properties.map((p) => ({
          ...p.definition,
          property_key: p.property_key,
        }))
      );
      
      // Calculate real statistics
      const totalProperties = properties.length;
      const propertiesWithCatalogs = properties.filter(
        p => p.definition.global_option_catalog_ref
      ).length;
      const propertiesWithDependencies = properties.filter(
        p => p.definition.calculation_dependencies
      ).length;
      const propertiesWithValidation = properties.filter(
        p => p.definition.validation_rules && Object.keys(p.definition.validation_rules).length > 0
      ).length;
      
      setStats({
        totalProperties,
        propertiesWithCatalogs,
        propertiesWithDependencies,
        propertiesWithValidation
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
      message.error('Failed to load property statistics');
    } finally {
      setLoading(false);
    }
  };

  const extractPropertyKeysFromJson = async (file: File): Promise<string[]> => {
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const keys = new Set<string>();

      const walk = (value: any) => {
        if (!value) return;
        if (Array.isArray(value)) {
          value.forEach(walk);
          return;
        }
        if (typeof value === 'object') {
          if (typeof value.property_key === 'string') {
            keys.add(value.property_key);
          }
          Object.values(value).forEach(walk);
        }
      };

      walk(json);

      // Fallback: if the JSON is a simple object of definitions keyed by property key
      if (keys.size === 0 && json && typeof json === 'object') {
        Object.keys(json).forEach((k) => keys.add(k));
      }

      return Array.from(keys);
    } catch (err) {
      console.warn('Unable to parse property keys from import file:', err);
      return [];
    }
  };

  const handleImport = async (file: File) => {
    try {
      setLoading(true);
      const extractedKeysPromise = extractPropertyKeysFromJson(file);
      const result = await GlobalPropertiesService.importGlobalProperties(file);
      message.success(`Imported ${result.imported} properties`);
      if (result.errors.length > 0) {
        message.warning(`${result.errors.length} errors during import`);
      }
      if (result.warnings.length > 0) {
        message.info(`${result.warnings.length} warnings during import`);
      }
      setShowImportModal(false);
      await loadStatistics();

      const extractedKeys = await extractedKeysPromise;
      if (extractedKeys.length > 0) {
        setAllPropertyKeys(extractedKeys);
      } else {
        message.info('Import completed, but no property keys were discovered in the file to display.');
      }
    } catch (error: any) {
      message.error(`Failed to import: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePropertyClick = () => {
    setShowCreatePropertyDialog(true);
    createPropertyForm.resetFields();
  };

  const handleCreatePropertySubmit = async () => {
    try {
      const values = await createPropertyForm.validateFields();
      const propertyKey = values.property_key.trim();

      if (!propertyKey) {
        message.error('Property key is required');
        return;
      }

      // Validate property key format
      if (!/^[a-z0-9_]+$/.test(propertyKey)) {
        message.error('Property key must be lowercase alphanumeric with underscores');
        return;
      }

      setLoading(true);
      setShowCreatePropertyDialog(false);

      // Create a minimal property definition
      const propertyDefinition: PropertyDefinition = {
        property_key: propertyKey,
        property_type: 'string', // Default type, user can change it
        description: '',
        input_source: 'user_input'
      };

      try {
        await GlobalPropertiesService.createPropertyDefinition(propertyDefinition);
        message.success('Property created successfully');

        // Reload statistics and property keys
        await loadStatistics();

        // Navigate to properties tab and select the new property
        setActiveTab('properties');
        
        // Use a small delay to ensure the table view is rendered
        setTimeout(() => {
          if (tableViewRef.current) {
            void tableViewRef.current.createAndNavigateToProperty(propertyKey);
          }
        }, 100);
      } catch (error: any) {
        message.error(`Failed to create property: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      // Form validation error
      console.error('Form validation error:', error);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Title level={2} style={{ margin: 0 }}>Global Properties Management</Title>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreatePropertyClick}
              >
                Create Property
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setShowImportModal(true)}
              >
                Import Properties
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadStatistics}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          </Space>
        </Card>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab} 
          size="large"
          items={[
            {
              key: 'overview',
              label: (
                <Space>
                  <DatabaseOutlined />
                  Overview
                </Space>
              ),
              children: (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Total Properties"
                          value={stats.totalProperties}
                          prefix={<DatabaseOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="With Option Catalogs"
                          value={stats.propertiesWithCatalogs}
                          prefix={<LinkOutlined />}
                          valueStyle={{ color: '#13c2c2' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="With Dependencies"
                          value={stats.propertiesWithDependencies}
                          prefix={<LinkOutlined />}
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="With Validation"
                          value={stats.propertiesWithValidation}
                          prefix={<CheckCircleOutlined />}
                          valueStyle={{ color: '#52c41a' }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  <Card style={{ marginTop: 16 }}>
                    <Title level={4}>Quick Actions</Title>
                    <Space wrap>
                      <Button
                        type="primary"
                        onClick={() => setActiveTab('properties')}
                      >
                        View All Properties
                      </Button>
                      <Button onClick={() => setActiveTab('relationships')}>
                        View Relationships
                      </Button>
                      <Button onClick={() => setActiveTab('option-catalogs')}>
                        Manage Option Catalogs
                      </Button>
                    </Space>
                  </Card>

                  <Card style={{ marginTop: 16 }}>
                    <Title level={4}>About Global Properties</Title>
                    <Text>
                      Global properties define property definitions and relationships independent of artifact structure,
                      enabling system-wide property management, validation, calculation, and state-based availability.
                      Properties defined in state templates, prime artifacts, and company artifacts reference these
                      global property definitions.
                    </Text>
                  </Card>
                </>
              )
            },
            {
              key: 'properties',
              label: (
                <Space>
                  <DatabaseOutlined />
                  Properties
                </Space>
              ),
              children: (
                <GlobalPropertyTableView 
                  ref={tableViewRef}
                  propertyKeys={allPropertyKeys.length > 0 ? allPropertyKeys : undefined}
                  showSplitPane={true}
                  onPropertySelect={(_propertyKey: string) => {
                    // Property selection handled internally in split-pane mode
                  }}
                  onEditProperty={(_propertyKey: string) => {
                    // Edit handled internally in split-pane mode
                  }}
                />
              )
            },
            {
              key: 'property-templates',
              label: (
                <Space>
                  <FileTextOutlined />
                  Property Templates
                </Space>
              ),
              children: (
                <Row gutter={16} style={{ height: 'calc(100vh - 280px)' }}>
                  <Col xs={24} lg={12}>
                    <PropertyTemplateList
                      onSelectTemplate={(template) => {
                        setSelectedPropertyTemplate(template);
                        setIsCreatingPropertyTemplate(false);
                      }}
                      onCreateTemplate={() => {
                        setSelectedPropertyTemplate(null);
                        setIsCreatingPropertyTemplate(true);
                      }}
                      selectedTemplateId={selectedPropertyTemplate?.id}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <PropertyTemplateEditor
                      template={selectedPropertyTemplate}
                      isCreating={isCreatingPropertyTemplate}
                      onSave={(template) => {
                        setSelectedPropertyTemplate(template);
                        setIsCreatingPropertyTemplate(false);
                      }}
                      onDelete={() => {
                        setSelectedPropertyTemplate(null);
                        setIsCreatingPropertyTemplate(false);
                      }}
                      onCancel={() => {
                        setIsCreatingPropertyTemplate(false);
                        if (!selectedPropertyTemplate) {
                          setSelectedPropertyTemplate(null);
                        }
                      }}
                    />
                  </Col>
                </Row>
              )
            },
            {
              key: 'state-templates',
              label: (
                <Space>
                  <FileTextOutlined />
                  State Templates
                </Space>
              ),
              children: (
                <TemplateManagementView 
                  onNavigateToProperty={(propertyKey: string) => {
                    setActiveTab('properties');
                    // Small delay to ensure the table view is rendered
                    setTimeout(() => {
                      if (tableViewRef.current) {
                        void tableViewRef.current.createAndNavigateToProperty(propertyKey);
                      }
                    }, 100);
                  }}
                  onNavigateToPropertiesTab={() => {
                    setActiveTab('properties');
                  }}
                />
              )
            },
            {
              key: 'relationships',
              label: (
                <Space>
                  <LinkOutlined />
                  Relationships
                </Space>
              ),
              children: (
                <Tabs
                  type="card"
                  items={[
                    {
                      key: 'graph',
                      label: 'Relationship Graph',
                      children: (
                        <GlobalPropertyRelationshipGraph
                          propertyKeys={allPropertyKeys.length > 0 ? allPropertyKeys : undefined}
                          propertyDefinitions={
                            allPropertyDefinitions.length > 0 ? allPropertyDefinitions : undefined
                          }
                        />
                      )
                    },
                    {
                      key: 'tree',
                      label: 'Property Tree',
                      children: (
                        <GlobalPropertyTreeView
                          propertyKeys={allPropertyKeys.length > 0 ? allPropertyKeys : undefined}
                          propertyDefinitions={
                            allPropertyDefinitions.length > 0 ? allPropertyDefinitions : undefined
                          }
                        />
                      )
                    }
                  ]}
                />
              )
            },
            {
              key: 'option-catalogs',
              label: (
                <Space>
                  <LinkOutlined />
                  Option Catalogs
                </Space>
              ),
              children: (
                <OptionCatalogsTabView />
              )
            },
            {
              key: 'validation',
              label: (
                <Space>
                  <CheckCircleOutlined />
                  Validation
                </Space>
              ),
              children: (
                <Card>
                  <Title level={4}>Property Validation Status</Title>
                  <Text type="secondary">
                    Validation status checking will be implemented to verify that all property tables
                    reference global property definitions correctly.
                  </Text>
                </Card>
              )
            }
          ]}
        />
      </Space>

      {/* Import Modal */}
      <Modal
        title="Import Global Properties"
        open={showImportModal}
        onCancel={() => setShowImportModal(false)}
        footer={null}
      >
        <Upload.Dragger
          accept=".json"
          beforeUpload={(file) => {
            handleImport(file);
            return false; // Prevent auto upload
          }}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag JSON catalog file to this area to upload</p>
          <p className="ant-upload-hint">
            Upload a JSON file containing global property definitions
          </p>
        </Upload.Dragger>
      </Modal>

      {/* Create Property Dialog */}
      <Modal
        title="Create New Property"
        open={showCreatePropertyDialog}
        onCancel={() => {
          setShowCreatePropertyDialog(false);
          createPropertyForm.resetFields();
        }}
        onOk={handleCreatePropertySubmit}
        okText="Create"
        cancelText="Cancel"
      >
        <Form
          form={createPropertyForm}
          layout="vertical"
        >
          <Form.Item
            name="property_key"
            label="Property Key"
            rules={[
              { required: true, message: 'Property key is required' },
              { pattern: /^[a-z0-9_]+$/, message: 'Property key must be lowercase alphanumeric with underscores' }
            ]}
          >
            <Input 
              placeholder="e.g., revenue, customer_name, order_status" 
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

